import { Play, Pause, Square, Minus, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { midiToNoteName } from '@/lib/pitch'
import type { PlaybackSpec } from '@/hooks/useWarmupPlayback'

interface PlayerControlsProps {
  spec: PlaybackSpec
  playback: {
    isPlaying: boolean
    isPaused: boolean
    stepIndex: number
    totalSteps: number
    nudge: number
    setNudge: (n: number) => void
    tempoQpm: number
    setTempoQpm: (n: number) => void
    play: () => void
    stop: () => void
    togglePause: () => void
  }
}

export default function PlayerControls({ spec, playback }: PlayerControlsProps) {
  const {
    isPlaying, isPaused, stepIndex, totalSteps,
    nudge, setNudge, tempoQpm, setTempoQpm, play, stop, togglePause,
  } = playback

  const startNote = midiToNoteName(spec.rangeLowMidi + nudge)
  const endNote = midiToNoteName(spec.rangeHighMidi + nudge)

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-center gap-3">
        {!isPlaying ? (
          <button
            onClick={play}
            className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-coil text-white shadow-lg transition-transform active:scale-95"
            aria-label="Play"
          >
            <Play className="ml-0.5 h-6 w-6" fill="currentColor" />
          </button>
        ) : (
          <>
            <button
              onClick={togglePause}
              className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-transform active:scale-95"
              aria-label={isPaused ? 'Resume' : 'Pause'}
            >
              {isPaused ? <Play className="ml-0.5 h-6 w-6" fill="currentColor" /> : <Pause className="h-6 w-6" fill="currentColor" />}
            </button>
            <button
              onClick={stop}
              className="inline-flex h-14 w-14 items-center justify-center rounded-full border-2 border-primary bg-card text-primary transition-transform active:scale-95"
              aria-label="Stop"
            >
              <Square className="h-5 w-5" fill="currentColor" />
            </button>
          </>
        )}
      </div>

      {isPlaying && totalSteps > 0 && (
        <p className="text-center spec-label text-coil">
          Step {Math.max(1, stepIndex + 1)} of {totalSteps}
        </p>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="rounded-lg border bg-card p-3">
          <p className="spec-label mb-2">Range · {startNote}–{endNote}</p>
          <div className="flex items-center justify-between">
            <button
              onClick={() => setNudge(nudge - 1)}
              disabled={isPlaying}
              className={cn('flex h-11 w-11 items-center justify-center rounded-md border', isPlaying && 'opacity-40')}
              aria-label="Start a semitone lower"
            >
              <Minus className="h-4 w-4" />
            </button>
            <span className="font-mono text-sm">{nudge > 0 ? `+${nudge}` : nudge} st</span>
            <button
              onClick={() => setNudge(nudge + 1)}
              disabled={isPlaying}
              className={cn('flex h-11 w-11 items-center justify-center rounded-md border', isPlaying && 'opacity-40')}
              aria-label="Start a semitone higher"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="rounded-lg border bg-card p-3">
          <p className="spec-label mb-2">Tempo · {tempoQpm} bpm</p>
          <input
            type="range"
            min={40}
            max={160}
            step={4}
            value={tempoQpm}
            onChange={(e) => setTempoQpm(Number(e.target.value))}
            className="w-full accent-[hsl(var(--coil))]"
            aria-label="Tempo"
          />
        </div>
      </div>
    </div>
  )
}
